import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Device, type DeviceInfo} from "@capacitor/device";
import {type Observable, Subject, lastValueFrom} from 'rxjs';
import {tap, catchError, filter, first, switchMap, distinctUntilChanged, throttleTime, map} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {LoggingService} from './logging.service';
import config from '../../../ngsw-config.json';
import type {ClientInfoInterface} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly called$ = new Subject<void>();

  constructor(
    private readonly logger: LoggingService,
    private readonly http: HttpClient,
    private readonly auth: AuthService,
  ) {
    // record version info from client
    this.called$.pipe(
      filter(() => this.auth.isOnline$.value),
      switchMap(() => this.auth.isActive$.pipe(
        distinctUntilChanged(),
        filter((active) => active),
        switchMap(() => this.auth.user$.pipe(
          map((user) => ({subjectId: user?.subjectId, resno: user?.resno})),
        )),
        first(),
      )),
    )
      .pipe(throttleTime(60000))
      .subscribe(async (user) => {
        try {
          const {subjectId, resno} = user;
          const deviceInfo = await Device.getInfo();
          await lastValueFrom(this.http.post<ClientInfoInterface<DeviceInfo>>('/api/client-info', {
              subjectId,
              resno,
              version: config.appData.version,
              metadata: deviceInfo,
            }),
          );
        } catch (e) {
          this.logger.error('record version error: ', e);
        }
      });
  }

  getData<T>(
    route: string,
    options?: {
      isAnonymous?: boolean;
      includeLastModified?: boolean;
      ifModifiedSince?: string;
    },
  ): Observable<{ data: T; lastModified: string } | T> {
    const inst = Math.round(Math.random() * 1000);
    this.logger.information(`getData called`, {loc: 'api: 61', inst, route});
    const headers = options?.ifModifiedSince ? {'If-Modified-Since': options.ifModifiedSince} : {};
    return this.auth.isActive$.pipe(
      filter((active) => active || options?.isAnonymous),
      first(),
      switchMap(() => this.http.get<T>(`/api/${route}`, {headers, observe: 'response'})),
      catchError((err) => {
        this.logger.error(`getData error`, {loc: 'api: 68', inst, err});
        throw err;
      }),
      map((response) =>
        options?.includeLastModified
          ? {
            data: response.body,
            lastModified: response.headers.get('Last-Modified'),
          }
          : response.body,
      ),
      tap((res) => {
        this.logger.information(`getData info`, {
          loc: 'api: 81',
          inst,
          route,
          result: route === 'projects' ? {} : res,
        });
      }),
      tap(() => this.called$.next()),
    );
  }

  putData<TBody, TResult>(route: string, model: string, data: TBody): Observable<TResult> {
    return this.auth.isActive$.pipe(
      filter((active) => active),
      first(),
      switchMap(() => this.http.put<TResult>(`/api/${route}${model}`, data)),
      catchError((err) => {
        throw err; // new Error(err);
      }),
      tap(() => this.called$.next()),
    );
  }

  postData<TBody, TResult>(route: string, body: TBody, authRequired: boolean = true): Observable<TResult> {
    const inst = Math.round(Math.random() * 1000);
    this.logger.information(`postData called`, {loc: 'api: 105', inst, route, body});
    return this.auth.isActive$.pipe(
      // tap(() => this.logger.information(`postData inside pipe`, {loc: 'api: 107', inst, body})),
      filter((active) => active || !authRequired),
      // tap(() => this.logger.information(`postData after filter`, {loc: 'api: 109', url, body})),
      first(),
      // tap(() => this.logger.information(`postData after filter() and first()`, {loc: 'api: 111', inst, body})),
      switchMap(() => this.http.post<TResult>(`/api/${route}`, body)),
      catchError((err) => {
        this.logger.error(`postData error`, {loc: 'api: 114', inst, err});
        throw err; // new Error(err);
      }),
      tap(() => this.logger.information(`postData finished`, {loc: 'api: 117', inst, body})),
      tap(() => this.called$.next()),
    );
  }

  patchData<TBody, TResult>(route: string, body: TBody): Observable<TResult> {
    return this.auth.isActive$.pipe(
      filter((active) => active),
      first(),
      switchMap(() => this.http.patch<TResult>(`/api/${route}`, body)),
      catchError((err) => {
        throw err; // new Error(err);
      }),
      tap(() => this.called$.next()),
    );
  }

  deleteData<TResult>(route: string, model: string, authRequired: boolean = true): Observable<TResult> {
    return this.auth.isActive$.pipe(
      filter((active) => active || !authRequired),
      first(),
      switchMap(() => this.http.delete<TResult>(`/api/${route}${model}`)),
      catchError((err) => {
        throw err; // new Error(err);
      }),
      tap(() => this.called$.next()),
    );
  }
}
